import React, { useMemo, useState } from "react";
import * as go from "gojs";
import { ReactDiagram } from "gojs-react";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";

type TypeOption = {
   id: string;
   name: string;
   color?: string;
};

function normalizeElementTypeReference(value: string) {
   return value.toLowerCase().replace(/[\s_-]+/g, "");
}

function elementTypeName(project: PLAProject, elementTypeId: string) {
   return project.elementTypes[elementTypeId]?.name || elementTypeId;
}

function isJernbanebyenElementType(project: PLAProject, element: PLAElementRecord) {
   const normalizedId = normalizeElementTypeReference(element.elementType);
   const normalizedName = normalizeElementTypeReference(elementTypeName(project, element.elementType));
   return normalizedId === "jernbanebyen" || normalizedName === "jernbanebyen";
}

function getReachableElementIds(rootElements: PLAElementRecord[], relations: PLARelationRecord[]) {
   const reachableIds = new Set(rootElements.map((element) => element.id));
   const pendingIds = rootElements.map((element) => element.id);
   while (pendingIds.length > 0) {
      const elementId = pendingIds.shift()!;
      relations.filter((relation) => relation.fromId === elementId).forEach((relation) => {
         if (reachableIds.has(relation.toId)) return;
         reachableIds.add(relation.toId);
         pendingIds.push(relation.toId);
      });
   }
   return reachableIds;
}

function createTypeOptions<T extends { name: string; sortOrder?: number; color?: string }>(types: Record<string, T>) {
   return Object.entries(types)
      .map(([id, type]) => ({ id, name: type.name || id, color: type.color }))
      .sort((left, right) => (types[left.id]?.sortOrder || 0) - (types[right.id]?.sortOrder || 0) || left.name.localeCompare(right.name));
}

function createDiagramModel(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], excludedElementTypeIds: Set<string>, excludedRelationTypeIds: Set<string>) {
   const visibleElements = elements.filter((element) => !excludedElementTypeIds.has(element.elementType));
   const visibleElementIds = new Set(visibleElements.map((element) => element.id));
   const visibleRelations = relations.filter((relation) => !excludedRelationTypeIds.has(relation.relationType) && visibleElementIds.has(relation.fromId) && visibleElementIds.has(relation.toId));
   const rootElements = visibleElements.filter((element) => isJernbanebyenElementType(project, element));
   const reachableElementIds = getReachableElementIds(rootElements, visibleRelations);
   const reachableElements = visibleElements.filter((element) => reachableElementIds.has(element.id));
   return {
      rootCount: rootElements.length,
      nodeDataArray: reachableElements.map((element) => ({
         key: element.id,
         text: element.name || Localization.getText("Unnamed element"),
         elementType: elementTypeName(project, element.elementType),
         color: project.elementTypes[element.elementType]?.color || "#2d8f85",
         isTreeExpanded: false
      })),
      linkDataArray: visibleRelations
         .filter((relation) => reachableElementIds.has(relation.fromId) && reachableElementIds.has(relation.toId))
         .map((relation) => ({
            key: relation.id,
            from: relation.fromId,
            to: relation.toId,
            text: project.relationTypes[relation.relationType]?.name || relation.relationType
         }))
   };
}

function setNodeTreeExpanded(node: go.Node, expanded: boolean) {
   node.isTreeExpanded = expanded;
   node.findTreeChildrenNodes().each((childNode) => setNodeTreeExpanded(childNode, expanded));
}

function toggleNodeTree(node: go.Node) {
   const diagram = node.diagram;
   if (!diagram) return;
   diagram.startTransaction("toggle PLA element tree");
   setNodeTreeExpanded(node, !node.isTreeExpanded);
   diagram.commitTransaction("toggle PLA element tree");
}

function initDiagram(onElementClick: (elementId: string) => void) {
   go.Diagram.licenseKey = SettingsManager.getSystemSetting("goJSLicenseKey_viamap_net");
   const $ = go.GraphObject.make;
   const diagram = $(go.Diagram, {
      allowDelete: false,
      allowClipboard: false,
      allowCopy: false,
      allowLink: false,
      allowInsert: false,
      allowTextEdit: false,
      scrollsPageOnFocus: false,
      initialDocumentSpot: go.Spot.Center,
      initialViewportSpot: go.Spot.Center,
      contentAlignment: go.Spot.Center,
      padding: new go.Margin(40, 50, 40, 50),
      layout: $(go.LayeredDigraphLayout, {
         direction: 90,
         layerSpacing: 57,
         columnSpacing: 24,
         setsPortSpots: false,
         aggressiveOption: go.LayeredDigraphLayout.AggressiveMore
      }),
      model: new go.GraphLinksModel({ linkKeyProperty: "key" })
   });

   diagram.nodeTemplate = $(go.Node, "Auto",
      {
         movable: true,
         cursor: "pointer",
         click: (_event, node) => toggleNodeTree(node.part as go.Node),
         doubleClick: (_event, node) => onElementClick(String(node.part?.data?.key || ""))
      },
      $(go.Shape, "RoundedRectangle",
         { stroke: "#1f3935", strokeWidth: 1.25, parameter1: 7 },
         new go.Binding("fill", "color")
      ),
      $(go.Panel, "Vertical", { margin: new go.Margin(6, 9, 6, 9), width: 135 },
         $(go.TextBlock,
            { stroke: "#ffffff", font: "bold 10pt sans-serif", maxLines: 2, overflow: go.TextBlock.OverflowEllipsis, textAlign: "center" },
            new go.Binding("text", "text")
         ),
         $(go.TextBlock,
            { stroke: "rgba(255,255,255,0.86)", font: "8pt sans-serif", margin: new go.Margin(3, 0, 0, 0), maxLines: 1, overflow: go.TextBlock.OverflowEllipsis, textAlign: "center" },
            new go.Binding("text", "elementType")
         )
      ),
      new go.Binding("isTreeExpanded", "isTreeExpanded").makeTwoWay()
   );

   diagram.linkTemplate = $(go.Link,
      { routing: go.Link.AvoidsNodes, curve: go.Link.JumpOver, corner: 7, toShortLength: 7 },
      $(go.Shape, { stroke: "#3f5652", strokeWidth: 1.4 }),
      $(go.Shape, { toArrow: "Triangle", stroke: "#3f5652", fill: "#3f5652" }),
      $(go.Panel, "Auto",
         { segmentIndex: NaN, segmentFraction: 0.5 },
         $(go.Shape, "RoundedRectangle", { fill: "#ffffff", stroke: "rgba(47,83,78,0.24)", parameter1: 4 }),
         $(go.TextBlock,
            { margin: new go.Margin(2, 4, 2, 4), stroke: "#294b46", font: "8pt sans-serif" },
            new go.Binding("text", "text")
         )
      )
   );

   return diagram;
}

function handleModelChange(_changes: any) {
   return undefined;
}

function toggleExcludedId(excludedIds: Set<string>, id: string, included: boolean) {
   const nextIds = new Set(excludedIds);
   if (included) {
      nextIds.delete(id);
   } else {
      nextIds.add(id);
   }
   return nextIds;
}

function TypeFilterList(props: {
   title: string;
   options: TypeOption[];
   excludedIds: Set<string>;
   onToggle: (id: string, included: boolean) => void;
}) {
   return (
      <div className="pla-tree-filter__group">
         <div className="pla-tree-filter__group-title">{props.title}</div>
         <div className="pla-tree-filter__options">
            {props.options.map((option) => (
               <label className="pla-tree-filter__option" key={option.id} title={option.name}>
                  <input type="checkbox" checked={!props.excludedIds.has(option.id)} onChange={(event) => props.onToggle(option.id, event.target.checked)} />
                  {option.color ? <span className="pla-tree-filter__swatch" style={{ backgroundColor: option.color }} /> : null}
                  <span className="pla-tree-filter__option-text">{option.name}</span>
               </label>
            ))}
         </div>
      </div>
   );
}

export function PLAElementTreeDiagram(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   onElementClick: (elementId: string) => void;
}) {
   const [filtersCollapsed, setFiltersCollapsed] = useState(false);
   const [excludedElementTypeIds, setExcludedElementTypeIds] = useState<Set<string>>(() => new Set());
   const [excludedRelationTypeIds, setExcludedRelationTypeIds] = useState<Set<string>>(() => new Set());
   const elementTypeOptions = useMemo(() => createTypeOptions(props.project.elementTypes), [props.project.elementTypes]);
   const relationTypeOptions = useMemo(() => createTypeOptions(props.project.relationTypes), [props.project.relationTypes]);
   const model = useMemo(
      () => createDiagramModel(props.project, props.elements, props.relations, excludedElementTypeIds, excludedRelationTypeIds),
      [props.project, props.elements, props.relations, excludedElementTypeIds, excludedRelationTypeIds]
   );

   return (
      <div className={`pla-tree-diagram-shell${filtersCollapsed ? " pla-tree-diagram-shell--filters-collapsed" : ""}`}>
         <aside className="pla-tree-filter-pane">
            <button className="pla-tree-filter__toggle" type="button" onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
               {Localization.getText(filtersCollapsed ? "Show filters" : "Hide filters")}
            </button>
            {filtersCollapsed ? null : (
               <div className="pla-tree-filter__content">
                  <TypeFilterList
                     title={Localization.getText("Element types")}
                     options={elementTypeOptions}
                     excludedIds={excludedElementTypeIds}
                     onToggle={(id, included) => setExcludedElementTypeIds((excludedIds) => toggleExcludedId(excludedIds, id, included))}
                  />
                  <TypeFilterList
                     title={Localization.getText("Relation types")}
                     options={relationTypeOptions}
                     excludedIds={excludedRelationTypeIds}
                     onToggle={(id, included) => setExcludedRelationTypeIds((excludedIds) => toggleExcludedId(excludedIds, id, included))}
                  />
               </div>
            )}
         </aside>
         <div className="pla-tree-diagram-stage">
            {model.rootCount === 0 ? (
               <div className="pla-tree-diagram__empty">{Localization.getText("No Jernbanebyen root element found")}</div>
            ) : (
               <ReactDiagram
                  initDiagram={() => initDiagram(props.onElementClick)}
                  divClassName="pla-tree-diagram"
                  nodeDataArray={model.nodeDataArray}
                  linkDataArray={model.linkDataArray}
                  onModelChange={handleModelChange}
               />
            )}
         </div>
      </div>
   );
}
